import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, isAdmin } from '../utils/userAuth';
import { getMarketplaceItems, addMarketplaceItem, deleteMarketplaceItem, getAllMarketplaceAdmin, adminDeleteListing } from '../utils/listingsStore';
import './Marketplace.css';

function Marketplace() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    itemType: '',
    condition: '',
    sort: 'createdAt'
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    itemType: 'textbook',
    condition: 'good',
    price: '',
    priceType: 'fixed',
    location: 'SPU Campus'
  });

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
    fetchProducts();
  }, [filters, activeTab]);

  const fetchProducts = () => {
    setLoading(true);
    try {
      let data;
      if (activeTab === 'admin' && user && isAdmin(user.email)) {
        data = getAllMarketplaceAdmin();
      } else if (activeTab === 'my-listings' && user) {
        data = getMarketplaceItems(filters).filter(p => p.seller?.id === user.id);
      } else {
        data = getMarketplaceItems(filters);
      }
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      addMarketplaceItem({
        ...formData,
        price: parseFloat(formData.price)
      }, user);

      setFormData({
        title: '',
        description: '',
        itemType: 'textbook',
        condition: 'good',
        price: '',
        priceType: 'fixed',
        location: 'SPU Campus'
      });
      setShowAddForm(false);
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
    }
  };

  const handleDelete = (productId) => {
    if (!user) return;

    if (window.confirm('Are you sure you want to delete this item?')) {
      if (activeTab === 'admin' && isAdmin(user.email)) {
        adminDeleteListing('marketplace', productId);
      } else {
        deleteMarketplaceItem(productId, user.id);
      }
      fetchProducts();
    }
  };

  const getConditionBadge = (condition) => {
    const badges = {
      'new': { label: 'New', color: '#00eb79' },
      'like-new': { label: 'Like New', color: '#06b6d4' },
      'good': { label: 'Good', color: '#667eea' },
      'fair': { label: 'Fair', color: '#f97316' },
      'poor': { label: 'Poor', color: '#ef4444' }
    };
    return badges[condition] || { label: condition, color: '#666' };
  };

  const getItemTypeIcon = (itemType) => {
    const icons = {
      'textbook': '📚',
      'electronics': '💻',
      'furniture': '🪑',
      'clothing': '👕',
      'supplies': '📦',
      'other': '🏷️'
    };
    return icons[itemType] || '🏷️';
  };

  return (
    <div className="marketplace-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h1 className="page-title">Marketplace</h1>
        <p className="page-subtitle">Buy and sell items on campus</p>
      </div>

      {/* Tabs */}
      <div className="listing-tabs">
        <button
          className={`tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => setActiveTab('browse')}
        >
          Browse All
        </button>
        {user && (
          <button
            className={`tab-btn ${activeTab === 'my-listings' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-listings')}
          >
            My Listings
          </button>
        )}
        {user && isAdmin(user.email) && (
          <button
            className={`tab-btn admin-tab ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin
          </button>
        )}
      </div>

      {/* Add Button */}
      {user && (
        <button className="fab-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕' : '+'}
        </button>
      )}

      {/* Quick Add Form */}
      {showAddForm && user && (
        <div className="quick-add-form">
          <h3>Sell an Item</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                name="title"
                placeholder="What are you selling?"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row">
              <textarea
                name="description"
                placeholder="Describe your item..."
                value={formData.description}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row two-col">
              <select name="itemType" value={formData.itemType} onChange={handleFormChange}>
                <option value="textbook">Textbook</option>
                <option value="electronics">Electronics</option>
                <option value="furniture">Furniture</option>
                <option value="clothing">Clothing</option>
                <option value="supplies">Supplies</option>
                <option value="other">Other</option>
              </select>
              <select name="condition" value={formData.condition} onChange={handleFormChange}>
                <option value="new">New</option>
                <option value="like-new">Like New</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
              </select>
            </div>
            <div className="form-row two-col">
              <div className="price-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  name="price"
                  placeholder="Price"
                  value={formData.price}
                  onChange={handleFormChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <select name="priceType" value={formData.priceType} onChange={handleFormChange}>
                <option value="fixed">Fixed Price</option>
                <option value="negotiable">OBO (Or Best Offer)</option>
              </select>
            </div>
            <div className="form-row">
              <input
                type="text"
                name="location"
                placeholder="Pickup location"
                value={formData.location}
                onChange={handleFormChange}
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                List Item
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <input
            type="text"
            name="search"
            placeholder="Search items..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select name="itemType" value={filters.itemType} onChange={handleFilterChange}>
            <option value="">All Types</option>
            <option value="textbook">Textbooks</option>
            <option value="electronics">Electronics</option>
            <option value="furniture">Furniture</option>
            <option value="clothing">Clothing</option>
            <option value="supplies">Supplies</option>
          </select>
        </div>
        <div className="filter-group">
          <select name="condition" value={filters.condition} onChange={handleFilterChange}>
            <option value="">Any Condition</option>
            <option value="new">New</option>
            <option value="like-new">Like New</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
          </select>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="loading-message">Loading items...</div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🛍️</div>
          <h3>No items found</h3>
          <p>{activeTab === 'my-listings' ? 'You haven\'t listed any items yet.' : 'Be the first to sell something!'}</p>
          {user && (
            <button className="btn-primary" onClick={() => setShowAddForm(true)}>
              List an Item
            </button>
          )}
        </div>
      ) : (
        <div className="products-grid">
          {products.map(product => (
            <div key={product._id} className="product-card">
              <div className="product-header">
                <span className="product-type">
                  {getItemTypeIcon(product.itemType)} {product.itemType}
                </span>
                <div className="header-actions">
                  <span
                    className="condition-badge"
                    style={{ backgroundColor: getConditionBadge(product.condition).color }}
                  >
                    {getConditionBadge(product.condition).label}
                  </span>
                  {(activeTab === 'my-listings' || activeTab === 'admin') && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(product._id)}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              <h3 className="product-title">{product.title}</h3>
              <p className="product-description">
                {product.description?.substring(0, 100)}...
              </p>

              <div className="product-meta">
                <span className="product-location">📍 {product.location}</span>
                <span className="product-seller">By {product.seller?.name || 'Student'}</span>
              </div>

              <div className="product-footer">
                <div className="product-price">
                  ${product.price}
                  {product.priceType === 'negotiable' && (
                    <span className="obo-badge">OBO</span>
                  )}
                </div>
                <button className="btn-contact">Contact Seller</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Login Prompt */}
      {!user && (
        <div className="login-prompt">
          <p>Want to sell items?</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Sign in to list
          </button>
        </div>
      )}
    </div>
  );
}

export default Marketplace;
